import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useCallback, useRef, useState } from 'react';
import type { ChangeEvent } from 'react';
import { REMARKS_PLACEHOLDER } from '../constants';
import {
  accordionStyles,
  accordianDetailsStyles,
  getAccordionSummaryStyles,
  remarksScrollContainerSx,
  remarksTextFieldSx,
} from '../components/Panel/styles';
import { abyssColors } from '@/theme/colors';

type JobPhaseRemarksProps = {
  value: string;
  onChange: (next: string) => void;
  readOnly?: boolean;
  disabled?: boolean;
};

export const JobPhaseRemarks = ({
  value,
  onChange,
  readOnly = false,
  disabled = false,
}: JobPhaseRemarksProps) => {
  const [expanded, setExpanded] = useState(true);
  const inputReference = useRef<HTMLTextAreaElement | null>(null);
  const scrollContainerReference = useRef<HTMLDivElement | null>(null);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const target = event.target as HTMLTextAreaElement;
      const cursorAtEnd = (target.selectionEnd ?? 0) === target.value.length;
      onChange(target.value);
      if (cursorAtEnd) {
        requestAnimationFrame(() => {
          const container = scrollContainerReference.current;
          if (container) {
            container.scrollTop = container.scrollHeight;
          }
        });
      }
    },
    [onChange]
  );

  return (
    <Accordion
      expanded={expanded}
      onChange={() => setExpanded(!expanded)}
      disableGutters
      sx={accordionStyles}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={getAccordionSummaryStyles(expanded)}>
        <Typography
          variant="h6"
          sx={{ fontWeight: 700, fontSize: '1.4rem', color: abyssColors.primary[500] }}
        >
          Job Phase Remarks
        </Typography>
      </AccordionSummary>
      <AccordionDetails
        sx={{
          ...accordianDetailsStyles,
          backgroundColor: 'background.paper',
          p: 2,
        }}
      >
        <Stack spacing={2}>
          <Box
            ref={scrollContainerReference}
            sx={{
              ...remarksScrollContainerSx,
              ...(disabled ? { opacity: 0.7 } : {}),
            }}
          >
            <TextField
              fullWidth
              multiline
              value={value}
              onChange={handleChange}
              disabled={disabled || readOnly}
              placeholder={REMARKS_PLACEHOLDER}
              variant="standard"
              slotProps={{ input: { disableUnderline: true } }}
              inputRef={inputReference}
              sx={remarksTextFieldSx}
            />
          </Box>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};
